import coreApi from './coreApi';
import type { Verse } from '../../features/bible/types';

export class BibleApi {
  async getVerses(params: { book: string; chapter: string; verse?: string; verses?: string[]; version?: string }) {
    const defaultParams = {
      ...params,
      version: params.version || 'en-kjv'
    };
    try {
      console.log('Fetching verses for:', defaultParams);
      const response = await coreApi.post('/bible/verses', defaultParams);
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestData: defaultParams
      });
      throw error;
    }
  }

  async getChapterVerses(book: string, chapter: string, version: string): Promise<Record<string, string>> {
    try {
      console.log('Fetching verses for:', { book, chapter, version });
      
      // Get all verses for the chapter using batch fetching
      const response = await coreApi.post('/bible/verses', {
        book,
        chapter,
        verses: Array.from({ length: 200 }, (_, i) => i + 1), // Request all possible verses as numbers
        version: version || 'en-kjv'
      });

      console.log('Response:', response.data);

      // Convert response to the expected format
      const verses: Record<string, string> = {};
      if (response.data.content) {
        response.data.content.split('[').forEach((part: string) => {
          if (!part) return;
          const match = part.match(/(\d+)\]\s*(.*?)(?=\[\d|$)/);
          if (match) {
            const [, verseNum, text] = match;
            verses[`${book}-${chapter}-${verseNum}`] = text.trim();
          }
        });
      }
      return verses;
    } catch (error: any) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestData: {
          book,
          chapter,
          version
        }
      });
      throw error;
    }
  }

  async getVerseContent(book: string, chapter: string, verse: string, version: string = 'en-kjv'): Promise<string> {
    try {
      const response = await this.getVerses({
        book,
        chapter,
        verse,
        version
      });
      console.log('Response:', response);
      return response.content;
    } catch (error: any) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestData: {
          book,
          chapter,
          verse,
          version
        }
      });
      throw error;
    }
  }

  async getBatchVerseContent(book: string, chapter: string, verses: string[], version?: string): Promise<Record<string, string>> {
    try {
      const response = await this.getVerses({ book, chapter, verses, version });
      const verseContents: Record<string, string> = {};

      if (response.content) {
        if (verses.length === 1) {
          // Single verse response
          verseContents[`${book}-${chapter}-${verses[0]}`] = response.content;
        } else {
          // Multi-verse response
          response.content.split('[').forEach((part: string) => {
            if (!part) return;
            const match = part.match(/(\d+)\]\s*(.*?)(?=\[\d|$)/);
            if (match) {
              const [, verseNum, text] = match;
              verseContents[`${book}-${chapter}-${verseNum}`] = text.trim();
            }
          });
        }
      }

      return verseContents;
    } catch (error) {
      console.error('Error fetching verse content:', error);
      return {};
    }
  }

  async getVerseExplanation(verse: string) {
    try {
      const response = await coreApi.post('/bible/explain', { verse });
      console.log('Response:', response.data);
      return response.data.explanation;
    } catch (error: any) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestData: {
          verse
        }
      });
      throw error;
    }
  }

  async getVerseQuestions(verse: string) {
    try {
      const response = await coreApi.post('/bible/questions', { verse });
      console.log('Response:', response.data);
      return response.data.questions;
    } catch (error: any) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestData: {
          verse
        }
      });
      throw error;
    }
  }
  
  async getChapterCount(book: string): Promise<{ count: number }> {
    try {
      const response = await coreApi.get(`/bible/chapter-count/${book}`);
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestData: {
          book
        }
      });
      throw error;
    }
  }

  async getVerseDetails(book: string, chapter: string, verse: string, version?: string) {
    try {
      // Format book name: remove spaces and ensure proper hyphenation
      const formattedBook = book.replace(/\s*-\s*/, '-').replace(/\s+/g, '-').toLowerCase();
      
      const response = await coreApi.get(`/bible/verses/${formattedBook}/${chapter}/${verse}`, {
        params: { version }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestData: {
          book,
          chapter,
          verse,
          version
        }
      });
      throw error;
    }
  }

  private async getBookNumber(book: string): Promise<string | null> {
    // Simple mapping of book names to numbers
    const bookMap: Record<string, string> = {
      'genesis': '1', 'exodus': '2', 'leviticus': '3', 'numbers': '4', 'deuteronomy': '5',
      'joshua': '6', 'judges': '7', 'ruth': '8', '1-samuel': '9', '2-samuel': '10',
      '1-kings': '11', '2-kings': '12', '1-chronicles': '13', '2-chronicles': '14',
      'ezra': '15', 'nehemiah': '16', 'esther': '17', 'job': '18', 'psalms': '19',
      'proverbs': '20', 'ecclesiastes': '21', 'songofsolomon': '22', 'isaiah': '23',
      'jeremiah': '24', 'lamentations': '25', 'ezekiel': '26', 'daniel': '27',
      'hosea': '28', 'joel': '29', 'amos': '30', 'obadiah': '31', 'jonah': '32',
      'micah': '33', 'nahum': '34', 'habakkuk': '35', 'zephaniah': '36', 'haggai': '37',
      'zechariah': '38', 'malachi': '39', 'matthew': '40', 'mark': '41', 'luke': '42',
      'john': '43', 'acts': '44', 'romans': '45', '1-corinthians': '46', '2-corinthians': '47',
      'galatians': '48', 'ephesians': '49', 'philippians': '50', 'colossians': '51',
      '1-thessalonians': '52', '2-thessalonians': '53', '1-timothy': '54', '2-timothy': '55',
      'titus': '56', 'philemon': '57', 'hebrews': '58', 'james': '59', '1-peter': '60',
      '2-peter': '61', '1-john': '62', '2-john': '63', '3-john': '64', 'jude': '65',
      'revelation': '66'
    };

    return bookMap[book.toLowerCase()] || null;
  }
}

export const bibleApi = new BibleApi();